import React, { useState } from 'react';
import Button from '../common/Button';
import LoadingSpinner from '../common/LoadingSpinner';
import { coursesAPI } from '../../services/api';

export default function ScorecardUpload({ onDataExtracted, onCancel }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [extractedData, setExtractedData] = useState(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setError(`File size is ${(file.size / 1024 / 1024).toFixed(2)}MB. Maximum is 5MB. Please choose a smaller image or reduce quality in camera settings.`);
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      setError(`File type "${file.type}" not supported. Please select an image file (JPEG, PNG, or WEBP).`);
      return;
    }

    setSelectedFile(file);
    setError('');
    setExtractedData(null);

    // Create preview URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleExtract = async () => {
    if (!selectedFile) return;

    setIsProcessing(true);
    setError('');

    try {
      const response = await coursesAPI.parseScorecardImage(selectedFile);
      setExtractedData(response.data);
    } catch (err) {
      let errorMessage = 'Failed to parse scorecard.';

      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error. The AI service may not be configured correctly.';
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = 'Request timed out. Please try again with a smaller image.';
      } else if (!err.response) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      setError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUseData = () => {
    if (extractedData) {
      onDataExtracted(extractedData);
    }
  };

  const handleTryAnother = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setExtractedData(null);
    setError('');
  };

  return (
    <div className="space-y-4">
      {/* Instructions */}
      {!selectedFile && !extractedData && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-900 font-semibold mb-2">Tips for best results:</p>
          <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
            <li>Ensure scorecard is well-lit and in focus</li>
            <li>Capture the full scorecard including all 18 holes</li>
            <li>Avoid shadows or glare on the card</li>
            <li>Supported formats: JPG, PNG, WEBP (max 5MB)</li>
          </ul>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="bg-red-600 text-white px-4 py-4 border-2 border-red-700 rounded-lg shadow-lg">
          <p className="font-bold text-lg mb-2">⚠️ Error</p>
          <p className="text-sm">{error}</p>
          <button
            onClick={() => setError('')}
            className="mt-3 px-3 py-1 bg-white text-red-600 rounded text-sm font-semibold hover:bg-red-50"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* File selection */}
      {!selectedFile && !extractedData && (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <input
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileSelect}
            className="hidden"
            id="scorecard-upload"
          />
          <label htmlFor="scorecard-upload" className="cursor-pointer">
            <div className="text-gray-600">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 48 48"
              >
                <path
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <p className="mt-2 text-sm font-semibold">Click to upload scorecard photo</p>
              <p className="mt-1 text-xs text-gray-500">or drag and drop</p>
            </div>
          </label>
        </div>
      )}

      {/* Image preview */}
      {selectedFile && previewUrl && !extractedData && (
        <div className="space-y-4">
          <div className="border border-gray-300 rounded-lg overflow-hidden">
            <img
              src={previewUrl}
              alt="Scorecard preview"
              className="w-full max-h-80 object-contain bg-gray-50"
            />
          </div>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="primary"
              onClick={handleExtract}
              disabled={isProcessing}
              className="flex-1"
            >
              {isProcessing ? (
                <span className="flex items-center justify-center gap-2">
                  <LoadingSpinner size="sm" />
                  Analyzing scorecard...
                </span>
              ) : (
                '📸 Extract Data'
              )}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={handleTryAnother}
              disabled={isProcessing}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Extracted data review */}
      {extractedData && (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-900 font-semibold">✓ Data extracted successfully!</p>
            <p className="text-sm text-green-800 mt-1">Review the data below and click "Use This Data" to populate the form.</p>
          </div>

          {/* Course info */}
          <div className="border border-gray-300 rounded-lg p-4">
            <h3 className="font-bold text-gray-900 mb-2">Course Information</h3>
            <div className="space-y-1 text-sm">
              <p><span className="font-semibold">Name:</span> {extractedData.name}</p>
              {extractedData.location && (
                <p><span className="font-semibold">Location:</span> {extractedData.location}</p>
              )}
            </div>
          </div>

          {/* Holes table */}
          <div className="border border-gray-300 rounded-lg overflow-hidden">
            <div className="bg-gray-100 px-4 py-2 border-b border-gray-300">
              <h3 className="font-bold text-gray-900">18 Holes</h3>
            </div>
            <div className="max-h-80 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Hole</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Par</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">SI</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Yardage</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {extractedData.holes.map((hole) => (
                    <tr key={hole.holeNumber} className="hover:bg-gray-50">
                      <td className="px-4 py-2">{hole.holeNumber}</td>
                      <td className="px-4 py-2">{hole.par}</td>
                      <td className="px-4 py-2">{hole.strokeIndex}</td>
                      <td className="px-4 py-2">{hole.yardage || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3">
            <Button
              type="button"
              variant="success"
              onClick={handleUseData}
              className="flex-1"
            >
              Use This Data
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={handleTryAnother}
            >
              Try Another Image
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
